import { DateTime, Duration } from "luxon";

/** Dates render in English whatever the viewer's locale, as they did under date-fns. */
const DATE_LOCALE = "en-US";

/**
 * Formats `date` on the viewer's clock with a Luxon format string, in English.
 *
 * Throws a RangeError for an invalid date, as date-fns did.
 */
export function formatLocalDate(date: Date, format: string): string {
  return formatValid(DateTime.fromJSDate(date), format);
}

/**
 * Formats `date` on the clock of the IANA zone `zone`, in English; an empty zone reads as UTC, as date-fns-tz read it.
 *
 * Throws a RangeError for an invalid date or an unknown zone.
 */
export function formatZonedDate(date: Date, zone: string, format: string): string {
  return formatValid(DateTime.fromJSDate(date, { zone: zone || "utc" }), format);
}

/**
 * A message's date and clock as the feed and audio player caption it: `Aug 11, 2026, 4:31 PM`.
 */
export function formatMessageTimestamp(date: Date): string {
  return formatLocalDate(date, "MMM d, yyyy, h:mm a");
}

/**
 * The first send of a recurring message in words: `Tuesday August 11th 2026 @ 4:31 PM`.
 */
export function formatRecurringStart(date: Date): string {
  const weekdayAndMonth = formatLocalDate(date, "EEEE MMMM");
  return `${weekdayAndMonth} ${ordinal(DateTime.fromJSDate(date).day)} ${formatLocalDate(date, "yyyy '@' h:mm a")}`;
}

/**
 * A 12-hour clock without padding: `4:31 PM`.
 */
export function formatClock12h(date: Date): string {
  return formatLocalDate(date, "h:mm a");
}

/**
 * A 12-hour clock padded to two-digit hours: `04:31 PM`.
 */
export function formatPaddedClock12h(date: Date): string {
  return formatLocalDate(date, "hh:mm a");
}

/**
 * The viewer's offset from GMT at `date`: `GMT-05:00`.
 */
export function formatGmtOffset(date: Date): string {
  return formatLocalDate(date, "'GMT'ZZ");
}

/**
 * A date with a two-digit day: `Aug 01, 2026`.
 */
export function formatPaddedDate(date: Date): string {
  return formatLocalDate(date, "MMM dd, yyyy");
}

/**
 * A call's length in words, omitting a zero part: `2 minutes 5 seconds`, `1 minute`, or empty for no length at all.
 */
export function formatCallDuration(totalSeconds: number): string {
  if (Number.isNaN(totalSeconds)) {
    return "";
  }
  const { minutes, seconds } = Duration.fromObject({ seconds: totalSeconds }).shiftTo("minutes", "seconds");
  return [countOf(minutes, "minute"), countOf(seconds, "second")].filter(Boolean).join(" ");
}

/**
 * Renders a valid `dateTime` in English, or throws the RangeError date-fns threw for an invalid one.
 */
function formatValid(dateTime: DateTime, format: string): string {
  if (!dateTime.isValid) {
    throw new RangeError("Invalid time value");
  }
  return dateTime.toFormat(format, { locale: DATE_LOCALE });
}

/**
 * `day` as an English ordinal: `1st`, `2nd`, `3rd`, `4th`, `11th`, `21st`.
 */
function ordinal(day: number): string {
  const lastTwoDigits = day % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/**
 * `count` of a unit in words, empty for none: `1 minute`, `5 seconds`.
 */
function countOf(count: number, unit: string): string {
  if (!count) {
    return "";
  }
  return count === 1 ? `1 ${unit}` : `${count} ${unit}s`;
}
